import React from 'react';
import { Box, Typography } from '@mui/material';
import DateTimePicker from 'react-datetime-picker';
import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';
import 'react-time-picker/dist/TimePicker.css';
import dayjs, { Dayjs } from 'dayjs';

/**
 * Design System Color Constants
 */
const COLORS = {
  ACCENT: '#005e5d',
  ACCENT_DARK: '#004d4c',
  ACCENT_LIGHT: '#005e5d14',
  SURFACE_SECONDARY: '#F2F3F5',
  BORDER: '#E6E7EB',
  BORDER_HOVER: '#D0D2D7',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
  DESTRUCTIVE: '#D32F2F',
  SUCCESS: '#49CC90',
  WARNING: '#FCA130',
};

export interface BaseDateTimePickerProps {
  /**
   * The value of the picker
   */
  value?: Dayjs | null;
  /**
   * Callback fired when the value changes
   */
  onChange?: (newValue: Dayjs | null) => void;
  /**
   * Label for the date time picker
   */
  label?: string;
  /**
   * Helper text displayed below
   */
  helperText?: string;
  /**
   * Maximum width of the component
   */
  maxWidth?: string | number;
  /**
   * Views to enable (day, month, year, hours, minutes, seconds)
   */
  views?: ('year' | 'month' | 'day' | 'hours' | 'minutes' | 'seconds')[];
  /**
   * Which view to open to initially
   */
  openTo?: 'year' | 'month' | 'day' | 'hours' | 'minutes' | 'seconds';
  /**
   * Format string for the display
   */
  format?: string;
  /**
   * The variant to use - desktop, mobile, or responsive
   */
  variantPicker?: 'desktop' | 'mobile' | 'static';
  /**
   * Whether the field is required
   */
  required?: boolean;
  /**
   * Whether to show error state
   */
  error?: boolean;
  /**
   * Whether the picker is disabled
   */
  disabled?: boolean;
  /**
   * Slot props for customization
   */
  slotProps?: any;
  /**
   * Additional props to pass to DateTimePicker
   */
  [key: string]: any;
}

/**
 * BaseDateTimePicker Component
 *
 * Premium date-time picker component following the Pulse API design system.
 * Supports controlled and uncontrolled value management with flexible view customization.
 *
 * Uses react-datetime-picker for reliable datetime selection.
 *
 * @example
 * ```tsx
 * // Basic uncontrolled picker
 * <BaseDateTimePicker
 *   label="Select Date and Time"
 *   defaultValue={new Date()}
 * />
 *
 * // Controlled picker
 * <BaseDateTimePicker
 *   label="Start DateTime"
 *   value={startDate}
 *   onChange={(newValue) => setStartDate(newValue)}
 * />
 *
 * // Date only
 * <BaseDateTimePicker
 *   label="Select Date"
 *   format="MM/dd/yyyy"
 * />
 *
 * // With helper text
 * <BaseDateTimePicker
 *   label="Event Date & Time"
 *   helperText="Select when the event will occur"
 *   required
 * />
 * ```
 */
export const BaseDateTimePicker = React.forwardRef<HTMLDivElement, BaseDateTimePickerProps>(
  (
    {
      label,
      helperText,
      maxWidth = '100%',
      views = ['year', 'month', 'day', 'hours', 'minutes'],
      openTo = 'day',
      format = 'MM/dd/yyyy h:mm a',
      disabled = false,
      required = false,
      error = false,
      variantPicker = 'desktop',
      value,
      onChange,
      slotProps,
      ...props
    },
    ref
  ) => {
    // Convert Dayjs to native Date if needed
    const dateValue = value && typeof value === 'object' && 'toDate' in value ? value.toDate() : value;

    // Determine which picker to show based on views
    const showTime = views?.includes('hours') || views?.includes('minutes');

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          maxWidth,
          width: '100%',
        }}
      >
        {label && (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 600,
              color: disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            {label}
            {required && <span style={{ color: COLORS.DESTRUCTIVE, marginLeft: '4px' }}>*</span>}
          </Typography>
        )}

        <Box
          sx={{
            '& .react-datetime-picker': {
              width: '100%',
            },
            '& .react-datetime-picker__wrapper': {
              fontFamily: '"Manrope", sans-serif',
              fontSize: '14px',
              border: `1px solid ${error ? COLORS.DESTRUCTIVE : COLORS.BORDER}`,
              borderRadius: '4px',
              padding: '8px 12px',
              backgroundColor: disabled ? COLORS.SURFACE_SECONDARY : '#fff',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: error ? COLORS.DESTRUCTIVE : COLORS.BORDER_HOVER,
              },
              '&:focus-within': {
                borderColor: error ? COLORS.DESTRUCTIVE : COLORS.ACCENT,
                borderWidth: '2px',
                paddingLeft: 'calc(12px - 1px)',
              },
            },
            '& .react-datetime-picker__inputGroup': {
              color: disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,
            },
            '& .react-datetime-picker__button': {
              color: disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,
              '&:hover': {
                backgroundColor: !disabled ? COLORS.ACCENT_LIGHT : 'transparent',
              },
            },
            '& .react-calendar': {
              fontFamily: '"Manrope", sans-serif',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
              marginTop: '4px',
            },
            '& .react-calendar__tile--active': {
              backgroundColor: COLORS.ACCENT,
              color: '#fff',
              '&:hover': {
                backgroundColor: COLORS.ACCENT_DARK,
              },
            },
            '& .react-time-picker': {
              fontFamily: '"Manrope", sans-serif',
            },
          }}
        >
          <DateTimePicker
            value={dateValue instanceof Date ? dateValue : null}
            onChange={(newValue: any) => {
              // Convert native Date back to Dayjs for callback
              if (onChange) {
                const dayjsValue = newValue ? dayjs(newValue) : null;
                onChange(dayjsValue);
              }
            }}
            format={format}
            disabled={disabled}
            clearIcon={null}
            {...props}
          />
        </Box>

        {helperText && (
          <Typography
            sx={{
              fontSize: '12px',
              color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_SECONDARY,
              fontFamily: '"Manrope", sans-serif',
              mt: -0.5,
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  }
);

BaseDateTimePicker.displayName = 'BaseDateTimePicker';

export default BaseDateTimePicker;
